import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { normalizeNetwork, getNetworkLabel } from "@/lib/networkLabels";

export interface CryptoNetwork {
  id: string;
  network: string;
  currency: string;
  label: string;
  tabValue: string;
  tokenContract: string | null;
  enabled: boolean;
  minDeposit: number;
  minWithdrawal: number;
  maxWithdrawal: number;
  confirmationsRequired: number;
  networkFeeEstimate: number;
  addressPlaceholder: string;
  depositWarning: string;
  adminFeeFlat: number;
  testnetMode: boolean;
  autoCreditDeposits: boolean;
  autoApproveWithdrawals: boolean;
  feeStrategy: string;
  waasProvider: string;
  allowUserFeeSelection: boolean;
  defaultFeePriority: "low" | "medium" | "high";
  networkFeeMarkupPercent: number | null;
  networkFeeMarkupFlat: number | null;
}

export function useCryptoNetworks(enabledOnly = true) {
  return useQuery({
    queryKey: ["crypto-networks", enabledOnly],
    queryFn: async () => {
      let query = supabase.from("crypto_settings").select("*").order("network");
      if (enabledOnly) query = query.eq("enabled", true);
      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((row: any): CryptoNetwork => {
        const network = normalizeNetwork(row.network);
        return {
        id: row.id,
        network,
        currency: row.currency,
        label: row.label || `${row.currency} (${getNetworkLabel(network)})`,
        tabValue: `${row.currency}-${network}`,
        tokenContract: row.token_contract,
        enabled: row.enabled,
        minDeposit: Number(row.min_deposit),
        minWithdrawal: Number(row.min_withdrawal),
        maxWithdrawal: Number(row.max_withdrawal),
        confirmationsRequired: row.confirmations_required,
        networkFeeEstimate: Number(row.network_fee_estimate),
        addressPlaceholder: row.address_placeholder || "Address...",
        depositWarning: row.deposit_warning || `Send only ${row.currency} on the ${getNetworkLabel(network)} network.`,
        adminFeeFlat: Number(row.admin_fee_flat),
        testnetMode: row.testnet_mode,
        autoCreditDeposits: row.auto_credit_deposits,
        autoApproveWithdrawals: row.auto_approve_withdrawals,
        feeStrategy: row.fee_strategy,
        waasProvider: row.waas_provider ?? "auto",
        allowUserFeeSelection: row.allow_user_fee_selection ?? true,
        defaultFeePriority: (row.default_fee_priority as "low" | "medium" | "high") ?? "medium",
        networkFeeMarkupPercent: row.network_fee_markup_percent == null ? null : Number(row.network_fee_markup_percent),
        networkFeeMarkupFlat: row.network_fee_markup_flat == null ? null : Number(row.network_fee_markup_flat),
        };
      });
    },
    staleTime: 60_000,
    refetchOnMount: "always",
  });
}

/** Find the tab value for a given currency (used by Wallets page) */
export function getTabValueForCurrency(networks: CryptoNetwork[], currency: string): string | null {
  const match = networks.find((n) => n.currency === currency);
  return match?.tabValue ?? null;
}
